import { Autocomplete, TextField } from '@mui/material';

import { Checkbox } from '@design-system/components/checkbox/checkbox';

import type { WorkoutOption } from '../../model/AutomationWorkouts.model';
import type { AutomationDetailsViewModel } from '../../view-model/AutomationDetails.viewModel';
import { useTrialNurtureConfigViewModel } from '../../view-model/TrialNurtureConfig.viewModel';
import * as styles from '../Automations.view.css';

type TrialNurtureConfigViewProps = {
  creatorId: number;
  details: AutomationDetailsViewModel;
};

/** Figma screen 03. "My Start Here section" is not offered: the backend
 *  only knows a specific workout as the link target. */
export const TrialNurtureConfigView = ({
  creatorId,
  details,
}: TrialNurtureConfigViewProps) => {
  const vm = useTrialNurtureConfigViewModel(creatorId, details);
  const optionList: WorkoutOption[] =
    vm.options.type === 'fetched' ? vm.options.value : [];
  const selected: WorkoutOption | null = vm.selectedWorkoutId
    ? optionList.find((option) => option.id === vm.selectedWorkoutId) ?? {
        id: vm.selectedWorkoutId,
        name: vm.selectedWorkoutLabel ?? vm.selectedWorkoutId,
      }
    : null;
  const loadError = vm.options.type === 'fetch-error' ? vm.options.value : null;

  return (
    <div className={styles.configForm} data-testid="trial-config">
      <div className={styles.configCard}>
        <div className={styles.configCardHead}>
          <span className={styles.configCardTitle}>
            Which workout should new trial members start with?
          </span>
          <span className={styles.requiredTag}>Required</span>
        </div>
        <span className={styles.configCardHint}>
          Every message links here, so pick one anyone can do.
        </span>
        <Autocomplete<WorkoutOption, false, false, false>
          options={optionList}
          value={selected}
          loading={vm.options.type === 'fetching'}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
            </li>
          )}
          onChange={(_event, option) => vm.selectWorkout(option?.id ?? null)}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') vm.setSearch(value);
          }}
          filterOptions={(options) => options}
          noOptionsText={
            loadError
              ? "We couldn't load your workouts."
              : 'No published workouts match'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search your published workouts"
              inputProps={{
                ...params.inputProps,
                'aria-label':
                  'Which workout should new trial members start with?',
              }}
              error={Boolean(vm.validation)}
              helperText={vm.validation}
            />
          )}
        />
        {loadError ? (
          <button
            type="button"
            className={styles.viewInsights}
            onClick={() => void loadError.retry()}
          >
            Try again
          </button>
        ) : null}
        {vm.confirmation ? (
          <div className={styles.confirmation}>{vm.confirmation}</div>
        ) : null}
      </div>

      <div className={styles.configCard}>
        <div className={styles.configCardHead}>
          <span className={styles.configCardTitle}>Push notifications</span>
          <span className={styles.optionalTag}>Optional</span>
        </div>
        <span className={styles.configCardHint}>
          Three short pushes sit between the emails. They only reach members who
          allowed notifications.
        </span>
        <Checkbox
          label="Include the 3 push notifications"
          checked={vm.includePushes}
          onChange={vm.setIncludePushes}
          testId="trial-include-pushes"
        />
      </div>
    </div>
  );
};
